import { cx } from "@linaria/core"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { type Ref } from "react"

import { DropdownMenuStyles as styles } from "@/components/DropdownMenu/DropdownMenuStyles.ts"

import type { ComponentPropsWithoutRef, ComponentRef, HTMLAttributes, RefAttributes } from "react"


const DropdownMenu = DropdownMenuPrimitive.Root

const DropdownMenuSub = DropdownMenuPrimitive.Sub

type DropdownMenuTriggerProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.Trigger>>
}

const DropdownMenuTrigger = ({ children, className, ref, ...props }: DropdownMenuTriggerProps) => (
  <DropdownMenuPrimitive.Trigger
    ref={ref}
    className={cx(styles.Trigger, className)}
    {...props}
  >
    {children}
  </DropdownMenuPrimitive.Trigger>
)

type DropdownMenuSubTriggerProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.SubTrigger>>
}

const DropdownMenuSubTrigger = ({ children, className, ref, ...props }: DropdownMenuSubTriggerProps) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cx(styles.Trigger, className)}
    {...props}
  >
    {children}
  </DropdownMenuPrimitive.SubTrigger>
)

type DropdownMenuContentProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.Content>>
  inset?: boolean
  onPlaced?: () => void
}

const DropdownMenuContent = ({ sideOffset = 4, className, ref, ...props }: DropdownMenuContentProps) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cx(styles.Content, className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
)

type DropdownMenuSubContentProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.SubContent>>
  inset?: boolean
  onPlaced?: () => void
}

const DropdownMenuSubContent = ({ sideOffset = 4, className, ref, ...props }: DropdownMenuSubContentProps) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      sideOffset={sideOffset}
      className={cx(styles.Content, className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
)

type DropdownMenuItemProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.Item>>
  inset?: boolean
  linkInside?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DropdownMenuItem = ({ className, linkInside, ref, ...props }: DropdownMenuItemProps) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cx(styles.Item, className)}
    {...props}
  />
)

type DropdownMenuItemLikeProps = HTMLAttributes<HTMLDivElement> & RefAttributes<HTMLDivElement>

const DropdownMenuItemLike = ({ className, ref, ...props }: DropdownMenuItemLikeProps) => (
  <div
    ref={ref}
    className={cx(styles.Item, className)}
    {...props}
  />
)

type DropdownMenuLabelProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.Label>>
  inset?: boolean
}

const DropdownMenuLabel = ({ className, ref, ...props }: DropdownMenuLabelProps) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cx(styles.Label, className)}
    {...props}
  />
)

type DropdownMenuSeparatorProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.Separator>>
}

const DropdownMenuSeparator = ({ className, ref, ...props }: DropdownMenuSeparatorProps) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cx(styles.Separator, className)}
    {...props}
  />
)

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemLike,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
}
